import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, Plus, Trash, Info
} from '@phosphor-icons/react';

export default function CmsCopyrightTracker() {
  const [copyrights, setCopyrights] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    work_id: '',
    license_type: 'all_rights_reserved',
    copyright_holder: '',
    copyright_year: new Date().getFullYear(),
    permissions: '',
    restrictions: '',
    attribution_required: true,
    commercial_use_allowed: false,
    modifications_allowed: false,
  });

  useEffect(() => {
    loadCopyrights();
    loadStats();
  }, []);

  const loadCopyrights = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/copyright?limit=50`, {
        withCredentials: true,
      });
      setCopyrights(r.data.copyright_infos || []);
    } catch (e) {
      toast.error('Failed to load copyright info');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/copyright/stats/overview`, {
        withCredentials: true,
      });
      setStats(r.data);
    } catch (e) {
      console.error('Failed to load stats');
    }
  };

  const saveCopyright = async (e) => {
    e.preventDefault();
    if (!form.work_id || !form.copyright_holder) {
      toast.error('Work ID and copyright holder are required');
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/copyright`,
        form,
        { withCredentials: true }
      );
      toast.success('Copyright info saved');
      setForm({
        work_id: '',
        license_type: 'all_rights_reserved',
        copyright_holder: '',
        copyright_year: new Date().getFullYear(),
        permissions: '',
        restrictions: '',
        attribution_required: true,
        commercial_use_allowed: false,
        modifications_allowed: false,
      });
      setShowForm(false);
      loadCopyrights();
      loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteCopyright = async (copyrightId) => {
    if (!window.confirm('Delete this copyright info?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/admin/copyright/${copyrightId}`, {
        withCredentials: true,
      });
      toast.success('Copyright info deleted');
      loadCopyrights();
      loadStats();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="cms-copyright-tracker">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <ShieldCheck size={28} weight="duotone" className="text-primary" />
          Copyright Tracker
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage copyright information, licenses, and usage rights for literary works
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Works</p>
            <p className="text-2xl font-bold">{stats.total_works_with_copyright}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">All Rights Reserved</p>
            <p className="text-2xl font-bold">{stats.all_rights_reserved}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Creative Commons</p>
            <p className="text-2xl font-bold">{stats.creative_commons}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Public Domain</p>
            <p className="text-2xl font-bold">{stats.public_domain}</p>
          </div>
        </div>
      )}

      {/* Add Copyright Form */}
      {showForm ? (
        <form onSubmit={saveCopyright} className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Add Copyright Information</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground">
              Close
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Work ID *</label>
              <input
                value={form.work_id}
                onChange={(e) => setForm({ ...form, work_id: e.target.value })}
                placeholder="work_abc123"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                data-testid="work-id"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Copyright Holder *</label>
              <input
                value={form.copyright_holder}
                onChange={(e) => setForm({ ...form, copyright_holder: e.target.value })}
                placeholder="Author Name"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                data-testid="copyright-holder"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">License Type</label>
              <select
                value={form.license_type}
                onChange={(e) => setForm({ ...form, license_type: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                data-testid="license-type"
              >
                <option value="all_rights_reserved">All Rights Reserved</option>
                <option value="cc_by">CC BY (Attribution)</option>
                <option value="cc_by_sa">CC BY-SA (Attribution-ShareAlike)</option>
                <option value="cc_by_nc">CC BY-NC (Attribution-NonCommercial)</option>
                <option value="cc0">CC0 (Public Domain Dedication)</option>
                <option value="public_domain">Public Domain</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Copyright Year</label>
              <input
                type="number"
                value={form.copyright_year}
                onChange={(e) => setForm({ ...form, copyright_year: parseInt(e.target.value) })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.attribution_required}
                onChange={(e) => setForm({ ...form, attribution_required: e.target.checked })}
              />
              Attribution Required
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.commercial_use_allowed}
                onChange={(e) => setForm({ ...form, commercial_use_allowed: e.target.checked })}
              />
              Commercial Use Allowed
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.modifications_allowed}
                onChange={(e) => setForm({ ...form, modifications_allowed: e.target.checked })}
              />
              Modifications Allowed
            </label>
          </div>

          <button
            type="submit"
            className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold"
            data-testid="save-copyright-btn"
          >
            Save Copyright Info
          </button>
        </form>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-semibold"
          data-testid="add-copyright-btn"
        >
          <Plus size={16} /> Add Copyright Info
        </button>
      )}

      {/* Copyrights List */}
      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : copyrights.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <ShieldCheck size={48} weight="duotone" className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No copyright information added yet</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg divide-y divide-border">
          {copyrights.map((copyright) => (
            <div key={copyright.copyright_id} className="p-4" data-testid={`copyright-${copyright.copyright_id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-semibold">{copyright.work_title || copyright.work_id}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {copyright.author_name && `by ${copyright.author_name} · `}
                    {copyright.copyright_holder} © {copyright.copyright_year}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded">
                      {copyright.license_type.replace(/_/g, ' ').toUpperCase()}
                    </span>
                    {copyright.commercial_use_allowed && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                        Commercial
                      </span>
                    )}
                    {copyright.modifications_allowed && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                        Modifications OK
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => deleteCopyright(copyright.copyright_id)}
                  className="text-muted-foreground hover:text-red-600 p-1"
                >
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
